import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { apiFetch } from "../utils/apiFetch";

let appInsights: ApplicationInsights | null = null;
let initPromise: Promise<ApplicationInsights | null> | null = null;

export function initAppInsights(): Promise<ApplicationInsights | null> {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (appInsights) return Promise.resolve(appInsights);
    if (initPromise) return initPromise;

    initPromise = apiFetch("/api/config")
        .then((res) => res.json())
        .then((config) => {
            const connectionString = config.appInsightsConnectionString;
            if (!connectionString) return null;

            appInsights = new ApplicationInsights({
                config: {
                    connectionString,
                    enableAutoRouteTracking: true,
                    enableAjaxErrorStatusText: true,
                    enableCorsCorrelation: true,
                    enableUnhandledPromiseRejectionTracking: true,
                },
            });
            appInsights.addTelemetryInitializer((group) => {
                group.tags = group.tags ?? {};
                group.tags["ai.cloud.role"] = "nexus-frontend";
            });

            appInsights.loadAppInsights();
            appInsights.trackPageView();
            return appInsights;
        })
        .catch(() => null);

    return initPromise;
}

export const Telemetry = {
    trackEvent: (name: string, props?: Record<string, any>) => {
        appInsights?.trackEvent({ name, properties: props });
    },

    trackTrace: (message: string, severityLevel: 0 | 1 | 2 | 3 | 4 | 5 = 1, props?: Record<string, any>) => {
        appInsights?.trackTrace({ message, severityLevel, properties: props });
    },

    trackException: (error: Error, props?: Record<string, any>) => {
        appInsights?.trackException({ exception: error, properties: props });
    },
}

export type WorkflowCategory = "self_onboard" | "evaluation" | "chat";
export type WorkflowStep =
    | "tab_view"
    | "cta_click"
    | "submit_attempt"
    | "submit_success"
    | "submit_failure"
    | "view_opened";

export type WorkflowEventProperties = {
    event_category: WorkflowCategory;
    step: WorkflowStep;
    tab?: string;
    subtab?: string;
    entity_type?: string;
    assistant_name?: string;
    datasource_type?: string;
    dataset_id?: string;
    project_name?: string;
    status_code?: string;
    error_kind?: string;
    [key: string]: string | number | boolean | null | undefined;
};

export enum WorkflowEventName {
    OnboardingTabView = "onboarding_tab_view",
    OnboardingAgentSelected = "onboarding_agent_selected",
    OnboardingCreateAgentClick = "onboarding_create_agent_click",
    OnboardingAgentSubmitAttempt = "onboarding_agent_submit_attempt",
    OnboardingAgentSubmitSuccess = "onboarding_agent_submit_success",
    OnboardingAgentSubmitFailure = "onboarding_agent_submit_failure",
    OnboardingCreateDatasourceClick = "onboarding_create_datasource_click",
    OnboardingDatasourceSubmitAttempt = "onboarding_datasource_submit_attempt",
    OnboardingDatasourceSubmitSuccess = "onboarding_datasource_submit_success",
    OnboardingDatasourceSubmitFailure = "onboarding_datasource_submit_failure",
    OnboardingIngestionTriggerAttempt = "onboarding_ingestion_trigger_attempt",
    OnboardingIngestionTriggerSuccess = "onboarding_ingestion_trigger_success",
    OnboardingIngestionTriggerFailure = "onboarding_ingestion_trigger_failure",
    DatasetsTabView = "datasets_tab_view",
    DatasetViewOpened = "dataset_view_opened",
    DatasetCreateAttempt = "dataset_create_attempt",
    DatasetCreateSuccess = "dataset_create_success",
    DatasetCreateFailure = "dataset_create_failure",
    EvaluationSubtabView = "evaluation_subtab_view",
    EvaluationTriggerAttempt = "evaluation_trigger_attempt",
    EvaluationTriggerSuccess = "evaluation_trigger_success",
    EvaluationTriggerFailure = "evaluation_trigger_failure",
    EvaluationMetricsViewOpened = "evaluation_metrics_view_opened",
    EvaluationLogsViewOpened = "evaluation_logs_view_opened",
    EvaluationDashboardRunViewOpened = "evaluation_dashboard_run_view_opened",
    EvaluationDashboardExportClick = "evaluation_dashboard_export_click",
    EvaluationDashboardCompareOpened = "evaluation_dashboard_compare_opened",
    EvaluationDashboardLoadFailure = "evaluation_dashboard_load_failure",
    EvaluationDashboardRunsLoadFailure = "evaluation_dashboard_runs_load_failure",
    EvaluationDashboardRunDetailFailure = "evaluation_dashboard_run_detail_failure",
    EvaluationDashboardExportFailure = "evaluation_dashboard_export_failure",
    ChatFirstUserMessage = "chat_first_user_message",
    ChatFollowUpSuggestionsReady = "chat_follow_up_suggestions_ready",
    ChatFollowUpQuestionClick = "chat_follow_up_question_click",
    ChatFollowUpSuggestionsFailure = "chat_follow_up_suggestions_failure",
    DatasetViewFailure = "dataset_view_failure",
    DatasetCopyFailure = "dataset_copy_failure",
    DatasetDeleteFailure = "dataset_delete_failure",
    DatasetExampleAddFailure = "dataset_example_add_failure",
    DatasetExampleUpdateFailure = "dataset_example_update_failure",
    DatasetExampleDeleteFailure = "dataset_example_delete_failure",
}

export function trackWorkflowEvent(
    name: WorkflowEventName,
    properties: WorkflowEventProperties,
): void {
    Telemetry.trackEvent(name, properties);
}
